import { isPreFinishStatus, isStatusPollable } from '@pages/Library/utils/mediaStatus';
import { useLibraryFolder } from '@stores/Library/LibraryFolder';
import { mediaStatusPoller } from '@stores/MediaStatusPoller';
import { useUploadManager } from '@stores/UploadManager/UploadManager';
import { reaction } from 'mobx';
import { useEffect } from 'react';

/**
 * Keep this view's library registered with `mediaStatusPoller` while any of its
 * media is still in flight.
 *
 * Replaces `useStatusPillRefresh` (a 500ms timer **per visible card**) and
 * `usePeriodicMediaRefresh` (a 5s per-media fan-out over visible items). Both
 * scaled linearly with the number of visible cards, neither was capped, and in
 * grid view both were mounted for the same media with no shared throttle. The
 * poller fetches the whole library in one request, so cost is flat.
 *
 * Visibility is deliberately no longer consulted. It existed to narrow *which
 * per-media requests* to issue; with one library-level request there is nothing
 * to narrow, so an IntersectionObserver per card would buy nothing.
 *
 * Mount once per view, not per card.
 */
export const useMediaStatusPolling = () => {
  const folder = useLibraryFolder();
  const uploadManager = useUploadManager();

  useEffect(() => {
    let dispose: (() => void) | null = null;
    const release = () => {
      dispose?.();
      dispose = null;
    };

    // A MobX reaction rather than render-derived state: this must work whether
    // or not the host component is an `observer`, and it must not re-register
    // on every unrelated mutation of `files` (the poller writes to it, which
    // would otherwise feed back into a register/unregister loop every cycle).
    const stop = reaction(
      () => {
        const libraryId = folder.library.libraryId;
        if (!libraryId) return null;
        // `empty`/`uploading` only advance when this client finishes the
        // upload, so they count as pollable only while that upload is live.
        // Without this, one abandoned upload left at `empty` keeps the poller
        // running forever on a fully-processed library.
        //
        // `hasLiveUploadForMedia` is called lazily, only on the pre-finish
        // branch: it reads two observable records, and MobX tracks missing
        // keys too, so an eager call per file would make this derivation
        // depend on ~N extra keys and re-run on any unrelated upload write.
        //
        // Reads `data?.files` rather than the `files` computed, which slices,
        // omni-filters and sorts — none of which affects "can anything here
        // still move", and all of which would add dependencies.
        const hasPollable = (folder.data?.files ?? []).some(
          (f) =>
            isStatusPollable(f.status) ||
            (isPreFinishStatus(f.status) && uploadManager.hasLiveUploadForMedia(f._id)),
        );
        return hasPollable ? libraryId : null;
      },
      (libraryId) => {
        release();
        if (libraryId) dispose = mediaStatusPoller.register(libraryId);
      },
      { fireImmediately: true },
    );

    return () => {
      stop();
      release();
    };
  }, [folder, uploadManager]);
};
